import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { firmwareAPI } from '../services/api';
import websocketService from '../services/websocket';
import { Activity, RefreshCw, Loader2, CheckCircle2, XCircle, Clock, Eye, X as XIcon, RotateCcw, Trash2 } from 'lucide-react';
import { AdminModalSurface } from './AdminModal';

export default function UpgradeMonitor() {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ total: 0, inProgress: 0, completed: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const autoRefresh = true;
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    fetchUpgradeTasks();

    // Auto-refresh every 10 seconds
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchUpgradeTasks();
      }, 10000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
    // Auto-refresh deliberately depends only on its enabled state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  useEffect(() => {
    // WebSocket integration for real-time updates
    const handleUpgradeUpdate = (message) => {
      const data = message?.data || message || {};
      if (!data.taskId) {
        fetchUpgradeTasks();
        return;
      }

      setTasks((prev) => {
        const updated = prev.map((task) =>
          task.id === data.taskId
            ? {
                ...task,
                ...data,
                diagnostics: {
                  ...(task.diagnostics || {}),
                  ...(data.diagnostics || {}),
                  lastAckAt: data.lastAckAt || task.diagnostics?.lastAckAt,
                  lastVerReqAt: data.lastVerReqAt || task.diagnostics?.lastVerReqAt
                }
              }
            : task
        );
        calculateStats(updated);
        return updated;
      });
    };

    websocketService.on('firmware:upgrade:progress', handleUpgradeUpdate);
    websocketService.on('upgrade_started', handleUpgradeUpdate);
    websocketService.on('upgrade_progress', handleUpgradeUpdate);
    websocketService.on('upgrade_completed', handleUpgradeUpdate);
    websocketService.on('upgrade_failed', handleUpgradeUpdate);

    return () => {
      websocketService.off('firmware:upgrade:progress', handleUpgradeUpdate);
      websocketService.off('upgrade_started', handleUpgradeUpdate);
      websocketService.off('upgrade_progress', handleUpgradeUpdate);
      websocketService.off('upgrade_completed', handleUpgradeUpdate);
      websocketService.off('upgrade_failed', handleUpgradeUpdate);
    };
    // WebSocket subscriptions are installed once and cleaned up on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUpgradeTasks = async () => {
    try {
      const response = await firmwareAPI.getUpgradeTasks();
      if (response.data?.success) {
        const taskData = response.data.data || [];
        setTasks(taskData);
        calculateStats(taskData);
      }
    } catch (error) {
      console.error('[Upgrade Monitor] Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (taskData) => {
    const stats = {
      total: taskData.length,
      inProgress: taskData.filter((t) => ['Pending', 'InProgress'].includes(t.status)).length,
      completed: taskData.filter((t) => t.status === 'Completed').length,
      failed: taskData.filter((t) => t.status === 'Failed').length
    };
    setStats(stats);
  };

  const handleCancel = async (taskId) => {
    if (!window.confirm(t('firmware.monitor.cancelConfirm'))) {
      return;
    }

    try {
      const response = await firmwareAPI.cancelUpgrade(taskId);
      if (response.data?.success) {
        alert(t('firmware.monitor.cancelSuccess'));
        fetchUpgradeTasks();
      } else {
        alert(t('firmware.monitor.cancelFailed'));
      }
    } catch (error) {
      console.error('[Cancel Upgrade] Error:', error);
      alert(t('firmware.monitor.cancelFailed'));
    }
  };

  const handleRetry = async (taskId) => {
    if (!window.confirm(t('firmware.monitor.retryConfirm'))) {
      return;
    }

    try {
      const response = await firmwareAPI.retryUpgrade(taskId);
      if (response.data?.success) {
        alert(t('firmware.monitor.retrySuccess'));
        fetchUpgradeTasks();
      } else {
        alert(t('firmware.monitor.retryFailed'));
      }
    } catch (error) {
      console.error('[Retry Upgrade] Error:', error);
      alert(t('firmware.monitor.retryFailed'));
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm(t('firmware.monitor.deleteConfirm') || 'Delete this task?')) {
      return;
    }
    try {
      const response = await firmwareAPI.deleteUpgrade(taskId);
      if (response.data?.success) {
        fetchUpgradeTasks();
      } else {
        alert(t('firmware.monitor.deleteFailed') || 'Delete failed');
      }
    } catch (error) {
      console.error('[Delete Upgrade] Error:', error);
      alert(t('firmware.monitor.deleteFailed') || 'Delete failed');
    }
  };

  const handleViewDetails = (task) => {
    setSelectedTask(task);
    setShowDetailsModal(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      // Backend PascalCase values
      Pending: { bg: 'bg-slate-400/10', text: 'text-slate-400', icon: Clock, label: t('firmware.monitor.status.pending') },
      InProgress: { bg: 'bg-blue-400/10', text: 'text-blue-400', icon: Loader2, label: t('firmware.monitor.status.downloading'), spin: true },
      Completed: { bg: 'bg-emerald-400/10', text: 'text-emerald-400', icon: CheckCircle2, label: t('firmware.monitor.status.completed') },
      Failed: { bg: 'bg-rose-400/10', text: 'text-rose-400', icon: XCircle, label: t('firmware.monitor.status.failed') },
      Cancelled: { bg: 'bg-slate-400/10', text: 'text-slate-500', icon: XCircle, label: t('firmware.monitor.status.cancelled') || 'Cancelled' },
    };
    const config = statusConfig[status] || statusConfig.Pending;
    const Icon = config.icon;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} flex items-center gap-1.5 w-fit`}>
        <Icon className={`w-3.5 h-3.5 ${config.spin ? 'animate-spin' : ''}`} />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatHeartbeatAge = (seconds) => {
    if (seconds === null || seconds === undefined) return '-';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('firmware.monitor.title')}</h1>
          <p className="text-slate-400 mt-1">{t('firmware.monitor.subtitle')}</p>
        </div>
        <button
          onClick={() => fetchUpgradeTasks()}
          className="px-4 py-2 bg-slate-800 border border-white/10 text-white rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          {t('common.refresh')}
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('firmware.monitor.stats.totalTasks')}</p>
              <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
            </div>
            <Activity className="w-8 h-8 text-cyan-400" />
          </div>
        </div>
        <div className="bg-slate-800 border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('firmware.monitor.stats.inProgress')}</p>
              <p className="text-2xl font-bold text-blue-400 mt-1">{stats.inProgress}</p>
            </div>
            <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
          </div>
        </div>
        <div className="bg-slate-800 border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('firmware.monitor.stats.completed')}</p>
              <p className="text-2xl font-bold text-emerald-400 mt-1">{stats.completed}</p>
            </div>
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
        </div>
        <div className="bg-slate-800 border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('firmware.monitor.stats.failed')}</p>
              <p className="text-2xl font-bold text-rose-400 mt-1">{stats.failed}</p>
            </div>
            <XCircle className="w-8 h-8 text-rose-400" />
          </div>
        </div>
      </div>

      {/* Auto-refresh indicator */}
      {autoRefresh && (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('firmware.monitor.autoRefresh')}
        </div>
      )}

      {/* Tasks Table */}
      <div className="bg-slate-800 border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">{t('firmware.monitor.noTasks')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50 border-b border-white/5">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.monitor.table.taskId')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.monitor.table.targetUnits')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.monitor.table.version')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.monitor.table.progress')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.monitor.table.status')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    TCP / DB
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.monitor.table.startTime')}
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.monitor.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {tasks.map((task) => (
                  <tr key={task.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <p className="text-white font-mono text-sm">#{String(task.id).slice(0, 8)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-300">{task.unit?.deviceName || task.unit?.deviceId || task.deviceId}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-white font-medium">{task.firmware?.version || task.version}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-400">{task.progress || 0}%</span>
                        </div>
                        <div className="w-32 bg-slate-900 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-cyan-400 h-full transition-all duration-300"
                            style={{ width: `${task.progress || 0}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(task.status)}</td>
                    <td className="px-6 py-4">
                      <div className="space-y-1 text-xs">
                        <div className={task.diagnostics?.tcpConnected ? 'text-emerald-300' : 'text-rose-300'}>
                          TCP {task.diagnostics?.tcpConnected ? 'Connected' : 'Disconnected'}
                        </div>
                        <div className="text-slate-400">
                          DB {task.diagnostics?.dbStatus || '-'} · HB {formatHeartbeatAge(task.diagnostics?.lastHeartbeatAgeSeconds)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300 text-sm">
                      {task.startedAt ? formatDate(task.startedAt) : formatDate(task.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewDetails(task)}
                          className="p-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
                          title={t('firmware.monitor.actions.viewDetails')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {['Pending', 'InProgress'].includes(task.status) && (
                          <button
                            onClick={() => handleCancel(task.id)}
                            className="p-2 bg-rose-400/10 text-rose-400 rounded-lg hover:bg-rose-400/20 transition-colors"
                            title={t('firmware.monitor.actions.cancel')}
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        )}
                        {['Failed', 'Cancelled'].includes(task.status) && (
                          <button
                            onClick={() => handleRetry(task.id)}
                            className="p-2 bg-cyan-400/10 text-cyan-400 rounded-lg hover:bg-cyan-400/20 transition-colors"
                            title={t('firmware.monitor.actions.retry')}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                        {['Failed', 'Cancelled', 'Completed'].includes(task.status) && (
                          <button
                            onClick={() => handleDelete(task.id)}
                            className="p-2 bg-rose-400/10 text-rose-400 rounded-lg hover:bg-rose-400/20 transition-colors"
                            title={t('firmware.monitor.actions.delete') || 'Delete'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedTask(null);
          }}
        />
      )}
    </div>
  );
}

function TaskDetailsModal({ task, onClose }) {
  const { t } = useTranslation();
  const diagnostics = task.diagnostics || {};
  const formatValueDate = (value) => value ? new Date(value).toLocaleString() : '-';

  return (
    <AdminModalSurface onClose={onClose}>
      <div className="bg-slate-800 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Task Details</h2>
            <p className="text-slate-400 mt-1">#{task.id}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <XIcon className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <p className="text-slate-400 text-sm mb-1">Version</p>
            <p className="text-white font-medium">{task.firmware?.version || task.version || '-'}</p>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">Device</p>
            <p className="text-white">{task.unit?.deviceName || task.unit?.deviceId || task.deviceId || '-'}</p>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">Status</p>
            <div className="mt-2">{task.status}</div>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">Progress</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-slate-900 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-cyan-400 h-full transition-all duration-300"
                  style={{ width: `${task.progress || 0}%` }}
                />
              </div>
              <span className="text-white font-medium">{task.progress || 0}%</span>
            </div>
          </div>
          <div>
            <p className="text-slate-400 text-sm mb-1">Started At</p>
            <p className="text-white">{formatValueDate(task.startedAt)}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div>
              <p className="text-slate-400 text-sm mb-1">TCP Online</p>
              <p className={diagnostics.tcpConnected ? 'text-emerald-300' : 'text-rose-300'}>
                {diagnostics.tcpConnected ? 'Connected' : 'Disconnected'}
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-sm mb-1">Computed Online</p>
              <p className={diagnostics.computedOnline ? 'text-emerald-300' : 'text-rose-300'}>
                {diagnostics.computedOnline ? 'Online' : 'Offline'}
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-sm mb-1">Heartbeat Fresh</p>
              <p className={diagnostics.heartbeatFresh ? 'text-emerald-300' : 'text-amber-300'}>
                {diagnostics.heartbeatFresh ? 'Fresh' : 'Stale'}
              </p>
            </div>
            <div>
              <p className="text-slate-400 text-sm mb-1">Online Reason</p>
              <p className="text-white">{diagnostics.onlineReason || '-'}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm mb-1">DB Status</p>
              <p className="text-white">{diagnostics.dbStatus || '-'}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm mb-1">Last Heartbeat</p>
              <p className="text-white">{formatValueDate(diagnostics.lastHeartbeatAt)}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm mb-1">Current Firmware</p>
              <p className="text-white">{diagnostics.currentFirmwareVersion || '-'}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm mb-1">Packets</p>
              <p className="text-white">{task.currentPacket || 0} / {task.totalPackets || 0}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm mb-1">Firmware File</p>
              <p className={diagnostics.firmwareFileExists ? 'text-emerald-300' : 'text-rose-300'}>
                {diagnostics.firmwareFileExists ? 'Available' : 'Missing'}
              </p>
            </div>
          </div>
          {(task.errorMessage || diagnostics.errorMessage || task.error) && (
            <div>
              <p className="text-slate-400 text-sm mb-1">Error</p>
              <p className="text-rose-300 break-words">{task.errorMessage || diagnostics.errorMessage || task.error}</p>
            </div>
          )}
          {diagnostics.pendingReason && (
            <div>
              <p className="text-slate-400 text-sm mb-1">Pending Reason</p>
              <p className="text-amber-300">{diagnostics.pendingReason}</p>
            </div>
          )}
          {diagnostics.firmwareFilePath && (
            <div>
              <p className="text-slate-400 text-sm mb-1">Server File Path</p>
              <p className="text-slate-300 break-all text-xs">{diagnostics.firmwareFilePath}</p>
            </div>
          )}
          {(diagnostics.lastAckAt || diagnostics.lastVerReqAt) && (
            <div>
              <p className="text-slate-400 text-sm mb-1">Last Device Upgrade Signal</p>
              <p className="text-white">
                ACK {formatValueDate(diagnostics.lastAckAt)} · VerReq {formatValueDate(diagnostics.lastVerReqAt)}
              </p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-white/10">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </AdminModalSurface>
  );
}
